import React, { ReactNode } from "react"
import { ColoredDialog } from "src/components/ColoredDialog"
import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
  useTheme,
  useMediaQuery,
  Theme
} from "@mui/material"
import ClearIcon from "@mui/icons-material/Clear"
import CheckIcon from "@mui/icons-material/Check"

interface TwoSidedModalProps {
  open: boolean
  // For simple icon display
  icon?: ReactNode
  // For complete custom left side content
  leftContent?: ReactNode
  leftBackgroundColor?: string
  rightBackgroundColor?: string
  title: string
  titleColor?: string
  titleStyle?: React.CSSProperties | object
  content: ReactNode
  contentStyle?: React.CSSProperties | object
  bullets?: ReactNode[]
  ending?: string
  link?: string
  buttonText?: string
  buttonStyle?: React.CSSProperties | object
  onButtonClick?: () => void
  onClose: () => void
  color?: "warning" | "info" | "primary"
  maxWidth?: string | number // Total dialog width (default: "1000px")
}

// Helper functions to reduce complexity
const getLeftBackgroundColor = (
  isMobile: boolean,
  rightBackgroundColor: string | undefined,
  leftBackgroundColor: string | undefined,
  palette: Theme["palette"],
  color: "warning" | "info" | "primary"
): string => {
  if (isMobile) {
    return rightBackgroundColor || palette[color].light || "#fff"
  }
  return leftBackgroundColor || palette[color].main
}

const getTitleStyle = (titleColor?: string, titleStyle?: React.CSSProperties | object) => ({
  font: "normal normal bold 26px/26px Inter",
  mb: 3,
  ...(titleColor && { color: titleColor }),
  ...titleStyle
})

const getContentStyle = (contentStyle?: React.CSSProperties | object) => ({
  font: "normal normal normal 14px/21px Inter",
  whiteSpace: "pre-line",
  ...contentStyle
})

const getButtonStyle = (buttonStyle?: React.CSSProperties | object) => ({
  font: "normal normal bold 18px/23px Inter",
  ...buttonStyle
})

const getButtonProps = (link?: string, onButtonClick?: () => void) => {
  if (link) {
    return {
      component: "a" as const,
      href: link,
      rel: "noopener noreferrer",
      target: "_blank"
    }
  }
  return {
    component: "button" as const,
    onClick: onButtonClick
  }
}

const wrapIconForMobile = (icon: ReactNode): ReactNode => {
  return (
    <Box sx={{ 
      width: "70px", 
      height: "70px", 
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      "& img": { 
        width: "100%", 
        height: "100%", 
        objectFit: "contain" 
      },
      "& > *": { 
        maxWidth: "70px", 
        maxHeight: "70px" 
      }
    }}>
      {icon}
    </Box>
  )
}

interface LeftSectionProps {
  isMobile: boolean
  spacing: Theme["spacing"]
  leftBgColor: string
  children: ReactNode
}

const LeftSection: React.FC<LeftSectionProps> = ({ isMobile, spacing, leftBgColor, children }) => (
  <Grid
    item
    xs={isMobile ? 12 : 4}
    sx={{
      display: "flex",
      justifyContent: isMobile ? "flex-start" : "center",
      alignItems: "center",
      p: isMobile ? spacing(4, 5, 3, 5) : "50px",
      backgroundColor: leftBgColor,
      flexShrink: 0,
      flexBasis: isMobile ? "auto" : "33.333%"
    }}
  >
    {children}
  </Grid>
)

interface RightSectionProps {
  isMobile: boolean
  spacing: Theme["spacing"]
  rightBackgroundColor?: string
  onClose: () => void
  title: string
  titleStyle: React.CSSProperties | object
  content: ReactNode
  contentStyle: React.CSSProperties | object
  bullets?: ReactNode[]
  ending?: string
  buttonProps: any
  buttonStyle: React.CSSProperties | object
  buttonText: string
  color: "warning" | "info" | "primary"
}

const RightSection: React.FC<RightSectionProps> = ({
  isMobile,
  spacing,
  rightBackgroundColor,
  onClose,
  title,
  titleStyle,
  content,
  contentStyle,
  bullets,
  ending,
  buttonProps,
  buttonStyle,
  buttonText,
  color
}) => (
  <Grid 
    item 
    xs={isMobile ? 12 : 8}
    sx={{
      flexGrow: 1,
      flexBasis: isMobile ? "auto" : "66.667%"
    }}
  >
    <DialogContent sx={{ 
      p: isMobile ? spacing(3, 5, 0, 5) : spacing(5, 5, 0, 5), 
      backgroundColor: rightBackgroundColor,
      position: "relative"
    }}>
      {!isMobile && (
        <IconButton
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 24,
            top: 24,
            color: (theme) => theme.palette.grey[500],
            "&:hover": { backgroundColor: "transparent" }
          }}
        >
          <ClearIcon />
        </IconButton>
      )}
      
      <Typography sx={titleStyle}>
        {title}
      </Typography>
      
      <Typography sx={contentStyle}>
        {content}
      </Typography>
      
      {bullets && bullets.length > 0 && (
        <List>
          {bullets.map((b, index) => <CustomLi key={index}>{b}</CustomLi>)}
        </List>
      )}
      
      {ending && (
        <Typography sx={{ font: "normal normal normal 14px/21px Inter" }}>
          <b>{ending}</b>
        </Typography>
      )}
    </DialogContent>
    
    <DialogActions sx={{ 
      p: isMobile ? spacing(3, 5, 4, 5) : spacing(4, 4, 4, 5), 
      backgroundColor: rightBackgroundColor 
    }}>
      <Button
        variant="contained"
        color={color}
        {...buttonProps}
        sx={buttonStyle}
      >
        {buttonText}
      </Button>
    </DialogActions>
  </Grid>
)

export const TwoSidedModal = ({ 
  open, 
  icon,
  leftContent,
  leftBackgroundColor,
  rightBackgroundColor,
  title,
  titleColor,
  titleStyle,
  content,
  contentStyle,
  bullets, 
  ending, 
  link, 
  buttonText = "Learn more",
  buttonStyle,
  onButtonClick,
  onClose,
  color = "primary",
  maxWidth = "1000px"
}: TwoSidedModalProps) => {
  const theme = useTheme()
  const { palette, spacing } = theme
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  
  const wrappedIcon = (icon && isMobile) ? wrapIconForMobile(icon) : icon
  const leftSectionContent = leftContent || wrappedIcon
  const leftBgColor = getLeftBackgroundColor(isMobile, rightBackgroundColor, leftBackgroundColor, palette, color)
  const buttonProps = getButtonProps(link, onButtonClick)

  return (
    <ColoredDialog
      open={open}
      color={color}
      disableScrollLock
      onClose={onClose}
      sx={{
        "& .MuiDialog-paper": {
          width: isMobile ? "90vw" : maxWidth,
          maxWidth: isMobile ? "90vw" : maxWidth
        }
      }}
    >
      <Grid container direction={isMobile ? "column" : "row"} wrap="nowrap">
        <LeftSection
          isMobile={isMobile}
          spacing={spacing}
          leftBgColor={leftBgColor}
        >
          {leftSectionContent}
        </LeftSection>

        <RightSection
          isMobile={isMobile}
          spacing={spacing}
          rightBackgroundColor={rightBackgroundColor}
          onClose={onClose}
          title={title}
          titleStyle={getTitleStyle(titleColor, titleStyle)}
          content={content}
          contentStyle={getContentStyle(contentStyle)}
          bullets={bullets}
          ending={ending}
          buttonProps={buttonProps}
          buttonStyle={getButtonStyle(buttonStyle)}
          buttonText={buttonText}
          color={color}
        />
      </Grid>
    </ColoredDialog>
  )
}

const CustomLi = ({ children }: { children: ReactNode }) => {
  return (
    <ListItem sx={{p: 0}}>
      <ListItemIcon sx={{ minWidth: 32 }}>
        <CheckIcon color="primary"/>
      </ListItemIcon>
      <ListItemText sx={{ "& .MuiTypography-root": { font: "normal normal normal 14px/28px Inter" } }}>
        {children}
      </ListItemText>
    </ListItem>
  )
}

// Backward compatibility export
export const TeaserModal = TwoSidedModal
